import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { IMAGES, bgStyle } from '@/config/images';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { 
  Printer, 
  ChevronRight, 
  ChevronLeft, 
  Check, 
  Rocket,
  Box,
  Thermometer,
  Settings,
  PartyPopper,
  Search,
  Cpu,
  Cog,
  Layers,
  Move,
  Zap,
  Grid3X3,
  Pencil,
  Trash2,
  Plus
} from 'lucide-react';

// Step definitions for custom setup
const STEPS = [
  { id: 'welcome', title: 'Welcome', icon: Rocket, description: 'Set up your 3D printer' },
  { id: 'basic', title: 'Basic Info', icon: Printer, description: 'Printer name and type' },
  { id: 'mechanics', title: 'Mechanics', icon: Cog, description: 'Motion system and motors' },
  { id: 'hotend', title: 'Hotend & Extruder', icon: Thermometer, description: 'Nozzle and extruder setup' },
  { id: 'bed', title: 'Build Plate', icon: Grid3X3, description: 'Bed size and features' },
  { id: 'features', title: 'Features', icon: Zap, description: 'Additional capabilities' },
  { id: 'complete', title: 'Complete', icon: PartyPopper, description: 'Review and save' },
];

// Motion system types
const MOTION_SYSTEMS = [
  { value: 'cartesian', label: 'Cartesian (Prusa-style)', desc: 'Bed moves Y, head moves X/Z' },
  { value: 'corexy', label: 'CoreXY', desc: 'Fixed bed, fast XY movement' },
  { value: 'belt_driven_lead_screws', label: 'Belt Driven Lead Screws', desc: 'CoreXY XY motion + belt-driven lead screw Z (Bambu-style)' },
  { value: 'bedslinger', label: 'Bed Slinger (Ender-style)', desc: 'Bed moves Y, gantry moves X/Z' },
  { value: 'delta', label: 'Delta', desc: 'Three vertical arms, round bed' },
  { value: 'hbot', label: 'H-Bot', desc: 'Similar to CoreXY, different belt path' },
  { value: 'other', label: 'Other/Custom', desc: 'Custom kinematics' },
];

// Stepper motor types
const MOTOR_TYPES = [
  { value: 'nema17', label: 'NEMA 17 (Standard)', desc: '42mm, most common' },
  { value: 'nema17_pancake', label: 'NEMA 17 Pancake', desc: 'Slim NEMA 17, lighter' },
  { value: 'nema14', label: 'NEMA 14', desc: '35mm, smaller printers' },
  { value: 'nema23', label: 'NEMA 23', desc: '57mm, larger/more torque' },
  { value: 'servo', label: 'Servo Motors', desc: 'High-end, closed loop' },
  { value: 'unknown', label: 'Unknown/Not Sure', desc: '' },
];

// Extruder types
const EXTRUDER_TYPES = [
  { value: 'direct_bmg', label: 'BMG Clone / Dual Gear Direct', desc: 'High grip, direct mount' },
  { value: 'direct_orbiter', label: 'Orbiter', desc: 'Lightweight direct drive' },
  { value: 'direct_titan', label: 'Titan / Titan Clone', desc: 'E3D style geared' },
  { value: 'direct_lgx', label: 'LGX / LGX Lite', desc: 'Bondtech double drive' },
  { value: 'direct_sherpa', label: 'Sherpa Mini', desc: 'Lightweight geared' },
  { value: 'direct_other', label: 'Other Direct Drive', desc: 'Generic direct mount' },
  { value: 'bowden_stock', label: 'Bowden (Stock/Clone)', desc: 'Remote extruder, PTFE tube' },
  { value: 'bowden_bmg', label: 'Bowden BMG', desc: 'BMG with bowden tube' },
  { value: 'bowden_other', label: 'Other Bowden', desc: 'Generic bowden setup' },
];

// Hotend types
const HOTEND_TYPES = [
  { value: 'e3d_v6', label: 'E3D V6 / Clone', desc: 'Classic all-metal' },
  { value: 'e3d_revo', label: 'E3D Revo', desc: 'Quick-change nozzle' },
  { value: 'dragon', label: 'Phaetus Dragon', desc: 'High flow option' },
  { value: 'dragonfly', label: 'Phaetus Dragonfly', desc: 'Bi-metal heatbreak' },
  { value: 'rapido', label: 'Phaetus Rapido', desc: 'Ultra high flow' },
  { value: 'mosquito', label: 'Slice Mosquito', desc: 'Premium hotend' },
  { value: 'mk8', label: 'MK8 / Stock Creality', desc: 'Basic PTFE-lined' },
  { value: 'microswiss', label: 'Micro Swiss All-Metal', desc: 'MK8 upgrade' },
  { value: 'volcano', label: 'E3D Volcano', desc: 'High flow, larger melt zone' },
  { value: 'other', label: 'Other/Custom', desc: '' },
];

// Nozzle materials
const NOZZLE_MATERIALS = [
  { value: 'brass', label: 'Brass', desc: 'Standard, best thermal, wears with abrasives' },
  { value: 'hardened_steel', label: 'Hardened Steel', desc: 'For abrasive filaments' },
  { value: 'stainless_steel', label: 'Stainless Steel', desc: 'Food-safe, harder than brass' },
  { value: 'ruby_tip', label: 'Ruby Tipped', desc: 'Premium, very durable' },
  { value: 'tungsten', label: 'Tungsten Carbide', desc: 'Extremely hard wearing' },
  { value: 'copper', label: 'Copper Plated', desc: 'Good thermal, some wear resistance' },
];

// Bed surfaces
const BED_SURFACES = [
  { value: 'glass', label: 'Glass', desc: 'Smooth finish, use adhesive' },
  { value: 'pei_smooth', label: 'PEI Smooth', desc: 'Good adhesion, releases when cool' },
  { value: 'pei_textured', label: 'PEI Textured', desc: 'Textured finish, great adhesion' },
  { value: 'buildtak', label: 'BuildTak / Similar', desc: 'Sticky surface sheet' },
  { value: 'magnetic_flex', label: 'Magnetic Flex Plate', desc: 'Removable, flexible' },
  { value: 'garolite', label: 'Garolite/G10', desc: 'Great for Nylon' },
  { value: 'aluminum', label: 'Bare Aluminum', desc: 'Needs adhesive/surface' },
  { value: 'other', label: 'Other', desc: '' },
];

// Leveling types
const LEVELING_TYPES = [
  { value: 'manual', label: 'Manual (Screws only)', desc: 'Traditional manual leveling' },
  { value: 'bltouch', label: 'BLTouch / 3DTouch', desc: 'Servo probe, popular' },
  { value: 'inductive', label: 'Inductive Probe', desc: 'Metal beds only' },
  { value: 'capacitive', label: 'Capacitive Probe', desc: 'Works with any surface' },
  { value: 'strain_gauge', label: 'Strain Gauge / Load Cell', desc: 'Prusa-style, nozzle-based' },
  { value: 'eddy_current', label: 'Eddy Current (Beacon)', desc: 'Fast, accurate' },
  { value: 'klicky', label: 'Klicky / Euclid Probe', desc: 'Magnetic attach probe' },
  { value: 'none', label: 'None', desc: 'No auto leveling' },
];

const PrinterSetupWizard = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  
  const [mode, setMode] = useState('select'); // 'select', 'preset', 'custom', 'manage'
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [presetPrinters, setPresetPrinters] = useState([]);
  const [existingPrinters, setExistingPrinters] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingPrinter, setEditingPrinter] = useState(null);

  const [formData, setFormData] = useState({
    // Basic Info
    name: '',
    brand: 'Custom',
    model: 'DIY Build',
    printer_type: 'fdm',
    
    // Mechanics
    motion_system: 'bedslinger',
    motor_type: 'nema17',
    linear_rails: false,
    belt_driven_z: false,
    dual_z: false,
    
    // Build Volume
    build_volume_x: 220,
    build_volume_y: 220,
    build_volume_z: 250,
    
    // Hotend & Extruder
    extruder_type: 'bowden_stock',
    extruder_count: 1,
    hotend_type: 'mk8',
    nozzle_diameter: 0.4,
    nozzle_material: 'brass',
    max_nozzle_temp: 260,
    all_metal_hotend: false,
    
    // Bed
    heated_bed: true,
    max_bed_temp: 100,
    bed_surface: 'glass',
    bed_leveling: 'manual',
    
    // Features
    enclosure: false,
    filament_runout_sensor: false,
    power_loss_recovery: false,
    multi_material: false,
    camera: false,
    led_lighting: false,
    
    // Firmware
    firmware: 'marlin',
    
    // Notes
    notes: ''
  });

  // Fetch presets and existing printers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [presetsRes, printersRes] = await Promise.all([
          axios.get(`${API}/printing3d/printers/presets`),
          token ? axios.get(`${API}/printing3d/printers`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
        ]);
        setPresetPrinters(presetsRes.data);
        const printerList = printersRes.data || [];
        setExistingPrinters(printerList);
        
        // Restore active printer from localStorage if it exists
        const storedPrinterId = localStorage.getItem('active_machine_printing3d');
        if (storedPrinterId && printerList.find(p => p.id === storedPrinterId)) {
          // Active printer is already set via the stored ID
        } else if (printerList.length > 0) {
          // Default to first printer
          localStorage.setItem('active_machine_printing3d', printerList[0].id);
        }
      } catch (error) {
        console.error('Failed to fetch printer data:', error);
      }
    };
    fetchData();
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset);
    setFormData({
      ...formData,
      name: `My ${preset.brand} ${preset.model}`,
      brand: preset.brand,
      model: preset.model,
      build_volume_x: preset.build_volume_x,
      build_volume_y: preset.build_volume_y,
      build_volume_z: preset.build_volume_z,
      max_nozzle_temp: preset.max_nozzle_temp,
      max_bed_temp: preset.max_bed_temp,
      heated_bed: preset.heated_bed,
      extruder_type: preset.direct_drive ? 'direct_other' : 'bowden_stock',
      enclosure: preset.enclosure,
      bed_leveling: preset.auto_bed_leveling ? 'bltouch' : 'manual',
    });
  };

  const progress = mode === 'custom' ? ((currentStep) / (STEPS.length - 1)) * 100 : 0;

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    } else {
      // Reset editing state when going back to select mode
      setEditingPrinter(null);
      setMode('select');
    }
  };

  const handleSave = async () => {
    if (!token) {
      toast.error('Please log in to save your printer');
      return;
    }

    setSaving(true);
    try {
      // Determine if direct drive based on extruder type
      const isDirectDrive = formData.extruder_type.startsWith('direct_');
      
      const printerData = {
        ...formData,
        direct_drive: isDirectDrive,
        auto_bed_leveling: formData.bed_leveling !== 'manual' && formData.bed_leveling !== 'none',
      };
      
      let savedPrinterId;
      if (editingPrinter) {
        await axios.put(`${API}/printing3d/printers/${editingPrinter.id}`, printerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPrinterId = editingPrinter.id;
        toast.success('Printer updated successfully!');
      } else {
        const response = await axios.post(`${API}/printing3d/printers`, printerData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        savedPrinterId = response.data.id;
        // Set newly created printer as active
        localStorage.setItem('active_machine_printing3d', savedPrinterId);
        toast.success('Printer saved successfully!');
      }
      navigate('/3d-printing');
    } catch (error) {
      console.error('Failed to save printer:', error);
      toast.error('Failed to save printer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePrinter = async (printerId) => {
    if (!confirm('Are you sure you want to delete this printer?')) return;
    
    try {
      await axios.delete(`${API}/printing3d/printers/${printerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const updatedPrinters = existingPrinters.filter(p => p.id !== printerId);
      setExistingPrinters(updatedPrinters);
      
      // If we deleted the active printer, clear localStorage or set to next available
      const activePrinterId = localStorage.getItem('active_machine_printing3d');
      if (activePrinterId === printerId) {
        if (updatedPrinters.length > 0) {
          localStorage.setItem('active_machine_printing3d', updatedPrinters[0].id);
        } else {
          localStorage.removeItem('active_machine_printing3d');
        }
      }
      toast.success('Printer deleted');
    } catch (error) {
      toast.error('Failed to delete printer');
    }
  };

  const [presetTypeFilter, setPresetTypeFilter] = useState('fdm');

  const filteredPresets = presetPrinters.filter(p => {
    const matchesSearch = searchQuery === '' ||
      p.brand.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.model.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = presetTypeFilter === 'all' || (p.printer_type || 'fdm') === presetTypeFilter;
    return matchesSearch && matchesType;
  });

  // Group presets by brand
  const groupedPresets = filteredPresets.reduce((acc, preset) => {
    if (!acc[preset.brand]) acc[preset.brand] = [];
    acc[preset.brand].push(preset);
    return acc;
  }, {});

  // ==========================================
  // MODE: SELECT (Initial Screen)
  // ==========================================
  if (mode === 'select') {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="printer-setup-wizard"
        style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
      >
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8 text-center">
            <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Printer className="w-10 h-10 text-orange-500" />
            </div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-2">CONFIGURE YOUR PRINTER</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              3D Printer Setup
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-2">Configure your printer for optimal print settings</p>
          </div>

          {/* Existing Printers */}
          {existingPrinters.length > 0 && (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-white">Your Printers ({existingPrinters.length})</CardTitle>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setMode('manage')}
                    className="border-zinc-700 text-zinc-400"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Manage All
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {existingPrinters.map((printer) => (
                    <div 
                      key={printer.id} 
                      className="p-4 bg-zinc-800/50 rounded-lg border border-zinc-700 flex items-center justify-between group hover:border-orange-500/50 transition-colors"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                          <Printer className="w-5 h-5 text-orange-500" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{printer.name}</div>
                          <div className="text-xs text-zinc-500">
                            {printer.brand} {printer.model} • {printer.nozzle_diameter}mm • {printer.direct_drive ? 'Direct Drive' : 'Bowden'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-zinc-700 text-zinc-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={() => {
                            setEditingPrinter(printer);
                            setFormData({
                              ...formData,
                              ...printer
                            });
                            setMode('custom');
                            setCurrentStep(1); // Skip welcome, go to basic info
                          }}
                        >
                          <Pencil className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Badge className="bg-green-600">Active</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Setup Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Quick Setup from Preset */}
            <Card 
              className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-orange-500 transition-all cursor-pointer"
              onClick={() => setMode('preset')}
            >
              <CardHeader>
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center mb-3">
                  <Search className="w-6 h-6 text-blue-500" />
                </div>
                <CardTitle className="text-white">Quick Setup</CardTitle>
                <CardDescription className="text-zinc-400">
                  Choose from 20+ popular printers (Ender, Prusa, Bambu Lab, etc.)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1 text-orange-400 text-sm">
                  <span>Select Preset</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>

            {/* Custom Build */}
            <Card 
              className="bg-black/70 backdrop-blur-sm border-zinc-700/50 hover:border-orange-500 transition-all cursor-pointer"
              onClick={() => { setMode('custom'); setCurrentStep(0); }}
            >
              <CardHeader>
                <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center mb-3">
                  <Cog className="w-6 h-6 text-orange-500" />
                </div>
                <CardTitle className="text-white">Custom Build</CardTitle>
                <CardDescription className="text-zinc-400">
                  Configure every detail: motors, extruder, hotend, bed, and more
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-1 text-orange-400 text-sm">
                  <span>Start Custom Setup</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // ==========================================
  // MODE: PRESET (Quick Selection)
  // ==========================================
  if (mode === 'preset') {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="printer-preset-select"
        style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
      >
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-3xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setMode('select')}
            className="mb-4 text-zinc-400"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
            <CardHeader>
              <CardTitle className="text-white">Select Your Printer</CardTitle>
              <CardDescription className="text-zinc-400">
                Choose from popular pre-configured printers
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Printer type tabs */}
              <div className="flex gap-2">
                {[
                  { id: 'fdm', label: 'FDM' },
                  { id: 'resin', label: 'Resin / MSLA' },
                  { id: 'sla', label: 'SLA' },
                  { id: 'all', label: 'All' },
                ].map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => setPresetTypeFilter(id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      presetTypeFilter === id
                        ? 'bg-orange-500 text-black'
                        : 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* Resin safety note */}
              {(presetTypeFilter === 'resin' || presetTypeFilter === 'sla') && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-red-900/30 border border-red-500/40 text-xs text-red-300">
                  <span className="font-semibold flex-shrink-0">⚠ Safety:</span>
                  <span>Resin/SLA printers use photopolymer resin — toxic uncured. Always wear nitrile gloves and eye protection. Work in ventilated area.</span>
                </div>
              )}

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  placeholder="Search by brand or model..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-zinc-800 border-zinc-700 text-white"
                />
              </div>

              {/* Printer List */}
              <div className="max-h-[400px] overflow-y-auto space-y-4 pr-2">
                {Object.entries(groupedPresets).map(([brand, printers]) => (
                  <div key={brand}>
                    <h4 className="text-sm font-medium text-zinc-400 mb-2">{brand}</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {printers.map((preset, idx) => (
                        <button
                          key={idx}
                          onClick={() => handlePresetSelect(preset)}
                          className={`p-3 rounded-lg border text-left transition-all ${
                            selectedPreset?.model === preset.model && selectedPreset?.brand === preset.brand
                              ? 'border-orange-500 bg-orange-500/10'
                              : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                          }`}
                        >
                          <div className="text-white font-medium">{preset.model}</div>
                          <div className="text-xs text-zinc-500">
                            {preset.build_volume_x}×{preset.build_volume_y}×{preset.build_volume_z}mm
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Save Selected */}
              {selectedPreset && (
                <div className="pt-4 border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-white font-medium">{formData.name}</div>
                      <div className="text-sm text-zinc-400">
                        {formData.build_volume_x}×{formData.build_volume_y}×{formData.build_volume_z}mm
                      </div>
                    </div>
                    <Badge className="bg-orange-600">Selected</Badge>
                  </div>
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full bg-orange-600 hover:bg-orange-500"
                  >
                    {saving ? 'Saving...' : 'Save Printer'}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // ==========================================
  // MODE: MANAGE (View/Edit Printers)
  // ==========================================
  if (mode === 'manage') {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="printer-manage"
        style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
      >
        <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={() => setMode('select')}
            className="mb-4 text-zinc-400"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-white">Manage Printers</h1>
            <Button 
              onClick={() => setMode('select')}
              className="bg-orange-600 hover:bg-orange-500"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Printer
            </Button>
          </div>

          {existingPrinters.length === 0 ? (
            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
              <CardContent className="py-12 text-center">
                <Printer className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No printers configured yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {existingPrinters.map((printer) => (
                <Card key={printer.id} className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-orange-500/20 rounded-lg flex items-center justify-center">
                          <Printer className="w-6 h-6 text-orange-500" />
                        </div>
                        <div>
                          <div className="text-white font-medium">{printer.name}</div>
                          <div className="text-sm text-zinc-400">
                            {printer.brand} {printer.model}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">
                            {printer.build_volume_x}×{printer.build_volume_y}×{printer.build_volume_z}mm • 
                            {printer.nozzle_diameter}mm nozzle • 
                            {printer.direct_drive ? 'Direct Drive' : 'Bowden'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-zinc-700"
                          onClick={() => {
                            setEditingPrinter(printer);
                            setFormData(printer);
                            setMode('custom');
                            setCurrentStep(0);
                          }}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="border-red-500 text-red-500 hover:bg-red-500/10"
                          onClick={() => handleDeletePrinter(printer.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==========================================
  // MODE: CUSTOM (Step-by-Step Wizard)
  // ==========================================
  const renderCustomStep = () => {
    switch (currentStep) {
      case 0: // Welcome
        return (
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Cog className="w-10 h-10 text-orange-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Custom Printer Setup</h2>
            <p className="text-zinc-400 max-w-md mx-auto mb-6">
              Configure every aspect of your DIY or custom-built 3D printer for accurate print calculations and troubleshooting.
            </p>
            <div className="p-4 bg-zinc-800/50 rounded-lg max-w-md mx-auto text-left">
              <p className="text-zinc-300 text-sm mb-2">This wizard will help you configure:</p>
              <ul className="text-sm text-zinc-400 space-y-1">
                <li>• Motion system & motors</li>
                <li>• Hotend & extruder type</li>
                <li>• Build plate & leveling</li>
                <li>• Additional features</li>
              </ul>
            </div>
          </div>
        );

      case 1: // Basic Info
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Printer Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="e.g., My Custom CoreXY"
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Brand (optional)</Label>
                <Input
                  value={formData.brand}
                  onChange={(e) => handleChange('brand', e.target.value)}
                  placeholder="e.g., Custom, Voron, RatRig"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Model (optional)</Label>
                <Input
                  value={formData.model}
                  onChange={(e) => handleChange('model', e.target.value)}
                  placeholder="e.g., V2.4, V-Core 3"
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
            </div>
          </div>
        );

      case 2: // Mechanics
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Motion System</Label>
              <div className="grid grid-cols-2 gap-2">
                {MOTION_SYSTEMS.map((system) => (
                  <button
                    key={system.value}
                    onClick={() => handleChange('motion_system', system.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      formData.motion_system === system.value
                        ? 'border-orange-500 bg-orange-500/10'
                        : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                    }`}
                  >
                    <div className="text-white font-medium text-sm">{system.label}</div>
                    <div className="text-xs text-zinc-500">{system.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Stepper Motors</Label>
              <Select value={formData.motor_type} onValueChange={(v) => handleChange('motor_type', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {MOTOR_TYPES.map((motor) => (
                    <SelectItem key={motor.value} value={motor.value} className="text-white">
                      {motor.label} {motor.desc && `- ${motor.desc}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-3 gap-2">
              {[
                { key: 'linear_rails', label: 'Linear Rails', desc: 'MGN rails installed' },
                { key: 'dual_z', label: 'Dual Z Motors', desc: 'Two Z steppers' },
                { key: 'belt_driven_z', label: 'Belt-Driven Z', desc: 'Belts instead of lead screws' },
              ].map((feature) => (
                <button
                  key={feature.key}
                  onClick={() => handleChange(feature.key, !formData[feature.key])}
                  className={`p-3 rounded-lg border text-center transition-all ${
                    formData[feature.key]
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                  }`}
                >
                  <div className="text-white text-sm font-medium">{feature.label}</div>
                  <div className="text-xs text-zinc-500">{feature.desc}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 3: // Hotend & Extruder
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Extruder Type</Label>
              <Select value={formData.extruder_type} onValueChange={(v) => handleChange('extruder_type', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[250px]">
                  <div className="px-2 py-1 text-xs text-orange-400 font-medium">Direct Drive</div>
                  {EXTRUDER_TYPES.filter(e => e.value.startsWith('direct')).map((ext) => (
                    <SelectItem key={ext.value} value={ext.value} className="text-white">
                      {ext.label}
                    </SelectItem>
                  ))}
                  <div className="px-2 py-1 text-xs text-blue-400 font-medium mt-2">Bowden</div>
                  {EXTRUDER_TYPES.filter(e => e.value.startsWith('bowden')).map((ext) => (
                    <SelectItem key={ext.value} value={ext.value} className="text-white">
                      {ext.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Hotend Type</Label>
              <Select value={formData.hotend_type} onValueChange={(v) => handleChange('hotend_type', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-[250px]">
                  {HOTEND_TYPES.map((hotend) => (
                    <SelectItem key={hotend.value} value={hotend.value} className="text-white">
                      {hotend.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Nozzle Diameter</Label>
                <Select value={String(formData.nozzle_diameter)} onValueChange={(v) => handleChange('nozzle_diameter', parseFloat(v))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {[0.15, 0.2, 0.25, 0.3, 0.35, 0.4, 0.5, 0.6, 0.8, 1.0, 1.2].map((size) => (
                      <SelectItem key={size} value={String(size)} className="text-white">
                        {size}mm {size === 0.4 ? '(Standard)' : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Nozzle Material</Label>
                <Select value={formData.nozzle_material} onValueChange={(v) => handleChange('nozzle_material', v)}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {NOZZLE_MATERIALS.map((mat) => (
                      <SelectItem key={mat.value} value={mat.value} className="text-white">
                        {mat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 mb-2 block">Max Nozzle Temp (°C)</Label>
                <Input
                  type="number"
                  value={formData.max_nozzle_temp}
                  onChange={(e) => handleChange('max_nozzle_temp', parseInt(e.target.value) || 260)}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              <div>
                <Label className="text-zinc-400 mb-2 block">Extruder Count</Label>
                <Select value={String(formData.extruder_count)} onValueChange={(v) => handleChange('extruder_count', parseInt(v))}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="1" className="text-white">Single Extruder</SelectItem>
                    <SelectItem value="2" className="text-white">Dual Extruder</SelectItem>
                    <SelectItem value="3" className="text-white">Triple Extruder</SelectItem>
                    <SelectItem value="4" className="text-white">Quad Extruder</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <button
              onClick={() => handleChange('all_metal_hotend', !formData.all_metal_hotend)}
              className={`w-full p-3 rounded-lg border text-left transition-all ${
                formData.all_metal_hotend
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">All-Metal Hotend</div>
                  <div className="text-xs text-zinc-500">Can print high-temp materials (ABS, Nylon, etc.)</div>
                </div>
                {formData.all_metal_hotend && <Check className="w-5 h-5 text-orange-500" />}
              </div>
            </button>
          </div>
        );

      case 4: // Bed
        return (
          <div className="space-y-4">
            <div>
              <Label className="text-zinc-400 mb-2 block">Build Volume (mm)</Label>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Input
                    type="number"
                    value={formData.build_volume_x}
                    onChange={(e) => handleChange('build_volume_x', parseInt(e.target.value) || 0)}
                    placeholder="X"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <span className="text-xs text-zinc-500 mt-1 block text-center">Width (X)</span>
                </div>
                <div>
                  <Input
                    type="number"
                    value={formData.build_volume_y}
                    onChange={(e) => handleChange('build_volume_y', parseInt(e.target.value) || 0)}
                    placeholder="Y"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <span className="text-xs text-zinc-500 mt-1 block text-center">Depth (Y)</span>
                </div>
                <div>
                  <Input
                    type="number"
                    value={formData.build_volume_z}
                    onChange={(e) => handleChange('build_volume_z', parseInt(e.target.value) || 0)}
                    placeholder="Z"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                  <span className="text-xs text-zinc-500 mt-1 block text-center">Height (Z)</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <button
                onClick={() => handleChange('heated_bed', !formData.heated_bed)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  formData.heated_bed
                    ? 'border-orange-500 bg-orange-500/10'
                    : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-white font-medium">Heated Bed</div>
                    <div className="text-xs text-zinc-500">Temperature-controlled build plate</div>
                  </div>
                  {formData.heated_bed && <Check className="w-5 h-5 text-orange-500" />}
                </div>
              </button>
              
              {formData.heated_bed && (
                <div>
                  <Label className="text-zinc-400 mb-2 block">Max Bed Temp (°C)</Label>
                  <Input
                    type="number"
                    value={formData.max_bed_temp}
                    onChange={(e) => handleChange('max_bed_temp', parseInt(e.target.value) || 100)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              )}
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Bed Surface</Label>
              <Select value={formData.bed_surface} onValueChange={(v) => handleChange('bed_surface', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {BED_SURFACES.map((surface) => (
                    <SelectItem key={surface.value} value={surface.value} className="text-white">
                      {surface.label} {surface.desc && `- ${surface.desc}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Bed Leveling</Label>
              <Select value={formData.bed_leveling} onValueChange={(v) => handleChange('bed_leveling', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {LEVELING_TYPES.map((level) => (
                    <SelectItem key={level.value} value={level.value} className="text-white">
                      {level.label} {level.desc && `- ${level.desc}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      case 5: // Features
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'enclosure', label: 'Enclosure', desc: 'Printer is enclosed' },
                { key: 'filament_runout_sensor', label: 'Filament Sensor', desc: 'Detects runout' },
                { key: 'power_loss_recovery', label: 'Power Loss Recovery', desc: 'Resume after outage' },
                { key: 'multi_material', label: 'Multi-Material', desc: 'MMU/AMS/Palette' },
                { key: 'camera', label: 'Camera', desc: 'Remote monitoring' },
                { key: 'led_lighting', label: 'LED Lighting', desc: 'Chamber lights' },
              ].map((feature) => (
                <button
                  key={feature.key}
                  onClick={() => handleChange(feature.key, !formData[feature.key])}
                  className={`p-4 rounded-lg border text-left transition-all ${
                    formData[feature.key]
                      ? 'border-orange-500 bg-orange-500/10'
                      : 'border-zinc-700 bg-zinc-800 hover:border-zinc-600'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-white font-medium">{feature.label}</div>
                      <div className="text-xs text-zinc-500">{feature.desc}</div>
                    </div>
                    {formData[feature.key] && <Check className="w-5 h-5 text-orange-500" />}
                  </div>
                </button>
              ))}
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Firmware</Label>
              <Select value={formData.firmware} onValueChange={(v) => handleChange('firmware', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="marlin" className="text-white">Marlin</SelectItem>
                  <SelectItem value="klipper" className="text-white">Klipper</SelectItem>
                  <SelectItem value="reprapfirmware" className="text-white">RepRapFirmware</SelectItem>
                  <SelectItem value="prusa" className="text-white">Prusa Firmware</SelectItem>
                  <SelectItem value="bambu" className="text-white">Bambu Firmware</SelectItem>
                  <SelectItem value="other" className="text-white">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-zinc-400 mb-2 block">Notes (optional)</Label>
              <Input
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any additional notes about your printer..."
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>
        );

      case 6: // Complete
        return (
          <div className="space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h3 className="text-xl font-bold text-white">Ready to Save!</h3>
              <p className="text-zinc-400">Review your printer configuration</p>
            </div>

            <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-white">{formData.name || 'Custom Printer'}</CardTitle>
                <CardDescription className="text-zinc-400">
                  {formData.brand} {formData.model}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-zinc-500">Motion:</span>
                    <span className="text-white ml-2 capitalize">{formData.motion_system.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Build Volume:</span>
                    <span className="text-white ml-2">{formData.build_volume_x}×{formData.build_volume_y}×{formData.build_volume_z}mm</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Extruder:</span>
                    <span className="text-white ml-2">{formData.extruder_type.startsWith('direct') ? 'Direct Drive' : 'Bowden'}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Hotend:</span>
                    <span className="text-white ml-2 capitalize">{formData.hotend_type.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Nozzle:</span>
                    <span className="text-white ml-2">{formData.nozzle_diameter}mm {formData.nozzle_material}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Max Temps:</span>
                    <span className="text-white ml-2">{formData.max_nozzle_temp}°C / {formData.max_bed_temp}°C</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Bed:</span>
                    <span className="text-white ml-2 capitalize">{formData.bed_surface.replace('_', ' ')}</span>
                  </div>
                  <div>
                    <span className="text-zinc-500">Leveling:</span>
                    <span className="text-white ml-2 capitalize">{formData.bed_leveling.replace('_', ' ')}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-700">
                  {formData.heated_bed && <Badge variant="outline" className="border-zinc-600">Heated Bed</Badge>}
                  {formData.enclosure && <Badge variant="outline" className="border-zinc-600">Enclosed</Badge>}
                  {formData.all_metal_hotend && <Badge variant="outline" className="border-zinc-600">All-Metal</Badge>}
                  {formData.linear_rails && <Badge variant="outline" className="border-zinc-600">Linear Rails</Badge>}
                  {formData.dual_z && <Badge variant="outline" className="border-zinc-600">Dual Z</Badge>}
                  {formData.filament_runout_sensor && <Badge variant="outline" className="border-zinc-600">Filament Sensor</Badge>}
                </div>
              </CardContent>
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="printer-custom-setup"
      style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}
    >
      <div className="fixed inset-0 bg-black/80 -z-10" style={{ pointerEvents: 'none' }} />
      <div className="max-w-2xl mx-auto">
        {/* Header showing Edit mode */}
        {editingPrinter && (
          <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg flex items-center gap-3">
            <Pencil className="w-5 h-5 text-blue-400" />
            <div>
              <span className="text-blue-400 font-medium">Editing: </span>
              <span className="text-white">{editingPrinter.name}</span>
            </div>
          </div>
        )}
        
        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-zinc-400">Step {currentStep + 1} of {STEPS.length}</span>
            <span className="text-sm text-zinc-400">{STEPS[currentStep].title}</span>
          </div>
          <Progress value={progress} className="h-2 bg-zinc-800" />
        </div>

        {/* Step Content */}
        <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white">{STEPS[currentStep].title}</CardTitle>
            <CardDescription className="text-zinc-400">{STEPS[currentStep].description}</CardDescription>
          </CardHeader>
          <CardContent>
            {renderCustomStep()}
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            className="border-zinc-700"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>

          {currentStep === STEPS.length - 1 ? (
            <Button
              onClick={handleSave}
              disabled={saving || !token || !formData.name}
              className="bg-orange-600 hover:bg-orange-500"
            >
              {saving ? 'Saving...' : editingPrinter ? 'Update Printer' : 'Save Printer'}
              <Check className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-orange-600 hover:bg-orange-500"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrinterSetupWizard;
